import { Box, Stack } from '@mui/material';
import { ChangeEvent, Dispatch, FC, SetStateAction, useMemo } from 'react';

import { fundApprovalStatus } from '@/api/fund/types';
import { Input, Select } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { FundsViewToggle } from '@/pages/fund-dashboard/modules/funds-view-toggle';

import { ResetFiltersButton } from '../../components/reset-filters-button';
import { fundsTableColumns } from '../data';

const statusOptions = [
  { value: fundApprovalStatus.Verified, label: 'Verified' },
  { value: fundApprovalStatus.ReVerificationPending, label: 'Re-verification Pending' },
  { value: fundApprovalStatus.AwaitingApproval, label: 'Awaiting Approval' },
  { value: fundApprovalStatus.Rejected, label: 'Rejected' },
  { value: fundApprovalStatus.Draft, label: 'Draft' },
  { value: fundApprovalStatus.Blocked, label: 'Blocked' },
];

const legalStructureOptions = [
  { value: 'LLC', label: 'LLC' },
  { value: 'LP', label: 'LP' },
];

const availabilityOptions = [
  { value: 'Open', label: 'Open' },
  { value: 'Closed', label: 'Closed' },
  { value: 'PreLaunch', label: 'Pre-Launch' },
  { value: 'Private', label: 'Private' },
];

const columnsGroups = {
  optional: { label: 'Optional columns' },
  required: { label: 'Always visible' },
};
const requiredColumns = ['name', 'entityName', 'actions'];
const columnsOptions = fundsTableColumns.map((column) => {
  const isRequiredColumn = requiredColumns.includes(column.key);
  return {
    value: column.key,
    label: column.header,
    group: isRequiredColumn ? 'required' : 'optional',
    disabled: isRequiredColumn,
  };
});

export interface FundsTabFiltersValue {
  search: string;
  status: string[];
  legalStructure: string[];
  investmentAvailabilityStatus: string[];
  columns: string[];
}

interface Props {
  value: FundsTabFiltersValue;
  setValue: Dispatch<SetStateAction<FundsTabFiltersValue>>;
  activeView: 'list' | 'gallery';
  setActiveView: (view: 'list' | 'gallery') => void;
}

export const fundsTabFiltersDefaultValue: Omit<FundsTabFiltersValue, 'columns'> = {
  search: '',
  status: [],
  legalStructure: [],
  investmentAvailabilityStatus: [],
};

export const FundsTabFilters: FC<Props> = ({ value, setValue, activeView, setActiveView }) => {
  const isMobile = useIsMobile();
  const { search, status, legalStructure, investmentAvailabilityStatus, columns } = value;

  const {
    onSearchChange,
    onStatusChange,
    onLegalStructureChange,
    onAvailabilityChange,
    onColumnsChange,
    resetFilters,
  } = useMemo(
    () => ({
      onSearchChange: (e: ChangeEvent<HTMLInputElement>) =>
        setValue((prev) => ({ ...prev, search: e.target.value })),
      onStatusChange: (v: string[]) => setValue((prev) => ({ ...prev, status: v })),
      onLegalStructureChange: (v: string[]) => setValue((prev) => ({ ...prev, legalStructure: v })),
      onAvailabilityChange: (v: string[]) =>
        setValue((prev) => ({ ...prev, investmentAvailabilityStatus: v })),
      onColumnsChange: (v: string[]) =>
        setValue((prev) => ({
          ...prev,
          columns: [...new Set(['name', 'entityName', 'actions', ...v])],
        })),
      resetFilters: () => setValue((prev) => ({ ...prev, ...fundsTabFiltersDefaultValue })),
    }),
    [setValue],
  );

  const showResetFilter =
    !!value.search ||
    value.status.length > 0 ||
    value.legalStructure.length > 0 ||
    value.investmentAvailabilityStatus.length > 0;

  return (
    <Stack
      alignItems={isMobile ? 'flex-start' : 'center'}
      justifyContent={isMobile ? 'initial' : 'space-between'}
      gap="40px"
      width="100%"
      flexDirection={isMobile ? 'column' : 'row'}
    >
      <Stack width={isMobile ? '100%' : '265px'}>
        <Input placeholder="Search" value={search} onChange={onSearchChange} />
      </Stack>

      <Box
        display="grid"
        gridTemplateColumns={isMobile ? '300px' : '150px 150px 150px 150px auto max-content'}
        gap="16px"
      >
        <Select
          multiple
          showSelectAll
          placeholder="Legal Structure"
          options={legalStructureOptions}
          value={legalStructure}
          onChange={onLegalStructureChange}
        />

        <Select
          multiple
          showSelectAll
          placeholder="Availability"
          options={availabilityOptions}
          value={investmentAvailabilityStatus}
          onChange={onAvailabilityChange}
        />

        <Select
          multiple
          showSelectAll
          placeholder="Status"
          options={statusOptions}
          value={status}
          onChange={onStatusChange}
        />

        <Select
          multiple
          showSelectAll
          showSearch
          placeholder="Columns"
          options={columnsOptions}
          groups={columnsGroups}
          value={columns}
          onChange={onColumnsChange}
        />

        {showResetFilter ? <ResetFiltersButton onClick={resetFilters} /> : <div />}

        <FundsViewToggle activeView={activeView} setActiveView={setActiveView} />
      </Box>
    </Stack>
  );
};
